import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    CircularProgress,
    Container,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DeleteIcon from '@mui/icons-material/Delete';
import ChecklistIcon from '@mui/icons-material/Checklist';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import StarIcon from '@mui/icons-material/Star';
import { MapPin, Todo, queryPackingOperations, PackingOperation } from '../schema';
import { getStationIconSrc } from '../utils';
import { countStations, countBins } from '../utils/stationBinCounts';
import { useEventDetails, useEventTodo } from '../context';
import NoEventError from './NoEventError';
import EventInventoryPackingList from './EventInventoryPackingList';
import EventInventoryWishList from './EventInventoryWishList';
import EventInventoryConsumables from './EventInventoryConsumables';
import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { EVENT_TAB_SCROLL_END_GAP_PX } from 'src/spacing';

interface Props {
    eventId: string;
}

const STORAGE_KEY = 'eventInventoryExpandedSections';

type SectionId = 'stations' | 'binTotals' | 'packingList' | 'consumables' | 'wishList';

function loadExpandedSections(): Set<SectionId> {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
            const parsed = JSON.parse(stored) as string[];
            const validSections = parsed.filter((s): s is SectionId =>
                ['stations', 'binTotals', 'packingList', 'consumables', 'wishList'].includes(s)
            );
            return new Set(validSections);
        }
    } catch {
        // Ignore parse errors
    }
    // Default: all expanded
    return new Set<SectionId>(['stations', 'binTotals', 'packingList', 'consumables', 'wishList']);
}

function saveExpandedSections(expanded: Set<SectionId>) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(expanded)));
    } catch {
        // Ignore storage errors
    }
}

export default function EventInventory(props: Props) {
    const { event, isLoading, error } = useEventDetails(props.eventId);
    const { todo: todos } = useEventTodo(props.eventId);
    const [expandedSections, setExpandedSections] = useState<Set<SectionId>>(() => loadExpandedSections());
    const navigate = useNavigate();
    const { data: operationsData } = queryPackingOperations();

    // Check if current event is a target of active operation
    const activeOperation = useMemo(() => {
        if (!operationsData?.packingOperations) return null;
        return operationsData.packingOperations.find((op: PackingOperation) => op.status === 'active') || null;
    }, [operationsData]);

    const isTargetOfActiveOp = useMemo(() => {
        if (!activeOperation || !event) return false;
        const targetEvents = activeOperation.targetEvents || [];
        return targetEvents.some((e: any) => e.id === props.eventId);
    }, [activeOperation, event, props.eventId]);

    const handleViewConfirmation = () => {
        if (activeOperation) {
            navigate(`/logistics/operation/${activeOperation.id}/confirmation`);
        }
    };

    useEffect(() => {
        saveExpandedSections(expandedSections);
    }, [expandedSections]);

    const handleAccordionChange = (sectionId: SectionId) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpandedSections(prev => {
            const next = new Set(prev);
            if (isExpanded) {
                next.add(sectionId);
            } else {
                next.delete(sectionId);
            }
            return next;
        });
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" padding={4}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Typography color="error">Error loading event details: {error.message}</Typography>;
    }

    if (!event) {
        return <NoEventError eventId={props.eventId} />;
    }

    const hasMapForEvent = (event.maps?.length ?? 0) > 0;

    // Build a map of mapPin IDs to their todos for filtering (stations / bin totals only when a map exists)
    const mapPinTodos = new Map<string, Todo[]>();
    let mapPins: MapPin[] = [];
    if (hasMapForEvent) {
        if (todos) {
            todos.forEach((todo: Todo) => {
                if (todo.mapPin?.id) {
                    const pinId = todo.mapPin.id;
                    if (!mapPinTodos.has(pinId)) {
                        mapPinTodos.set(pinId, []);
                    }
                    const pinList = mapPinTodos.get(pinId);
                    if (!pinList) throw new Error('EventInventory: invariant — pin todo list missing');
                    pinList.push(todo);
                }
            });
        }

        // Filter out mapPins that should not be counted:
        // 1. Exclude incomplete place-bin todos (completed place-bin todos should be counted)
        // 2. Exclude all pickup-needed todos (they disappear when completed, so never count them)
        mapPins = (event.maps ?? []).flatMap(map => map.pins || []);
        mapPins = mapPins.filter((pin: MapPin) => {
            const pinTodos = mapPinTodos.get(pin.id) || [];

            // Check for pickup-needed todos (exclude all, regardless of completion status)
            const hasPickupNeeded = pinTodos.some((todo: Todo) => todo.todoType?.name === 'pickup-needed');
            if (hasPickupNeeded) {
                return false;
            }

            // Check for incomplete place-bin todos (exclude only incomplete ones)
            const hasIncompletePlaceBin = pinTodos.some((todo: Todo) => todo.todoType?.name === 'place-bin' && !todo.completed);
            if (hasIncompletePlaceBin) {
                return false;
            }

            return true;
        });
    }

    const stationCounts = hasMapForEvent ? countStations(mapPins) : [];
    const binCounts = hasMapForEvent ? countBins(stationCounts) : countBins([]);
    const pinCount = mapPins.length;
    const totalBins = binCounts.all;

    return (
        <Container
            maxWidth="md"
            disableGutters
            sx={{
                display: 'flex',
                flexGrow: 1,
                flexDirection: 'column',
                alignItems: 'center',
                paddingBottom: `${EVENT_TAB_SCROLL_END_GAP_PX}px`,
                px: { xs: 2, sm: 3 },
                minWidth: 0,
                maxWidth: '100%',
                boxSizing: 'border-box',
            }}
        >
            {hasMapForEvent && (
                <>
                    {/* Stations Section */}
                    <Accordion
                        expanded={expandedSections.has('stations')}
                        onChange={handleAccordionChange('stations')}
                        sx={{
                            width: '100%',
                            mt: 2,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                            borderRadius: '8px',
                            border: '1px solid rgba(0,0,0,0.06)',
                            '&:before': {
                                display: 'none',
                            },
                        }}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                <LocationOnIcon />
                                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                    Stations
                                </Typography>
                                <Typography variant="body2" sx={{ ml: 'auto', color: 'text.secondary' }}>
                                    {pinCount}
                                </Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 0 }}>
                            <Paper
                                sx={{
                                    overflowX: 'auto',
                                    width: '100%',
                                    boxShadow: 'none',
                                    borderRadius: 0,
                                }}
                            >
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Icon</TableCell>
                                            <TableCell>Name</TableCell>
                                            <TableCell>Count</TableCell>
                                            <TableCell>Description</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {stationCounts.map(({ station, count }, key) => (
                                            <TableRow key={key}>
                                                <TableCell>
                                                    <img width="50px" height="50px" alt={station.name} src={getStationIconSrc(station)} />
                                                </TableCell>
                                                <TableCell>{station.name}</TableCell>
                                                <TableCell>{count}</TableCell>
                                                <TableCell>{station.description}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Paper>
                        </AccordionDetails>
                    </Accordion>

                    {/* Bin Totals Section */}
                    <Accordion
                        expanded={expandedSections.has('binTotals')}
                        onChange={handleAccordionChange('binTotals')}
                        sx={{
                            width: '100%',
                            mt: 2,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                            borderRadius: '8px',
                            border: '1px solid rgba(0,0,0,0.06)',
                            '&:before': {
                                display: 'none',
                            },
                        }}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                <DeleteIcon />
                                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                    Bin Totals
                                </Typography>
                                <Typography variant="body2" sx={{ ml: 'auto', color: 'text.secondary' }}>
                                    {totalBins}
                                </Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 0 }}>
                            <Paper
                                sx={{
                                    overflowX: 'auto',
                                    width: '100%',
                                    boxShadow: 'none',
                                    borderRadius: 0,
                                }}
                            >
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Black</TableCell>
                                            <TableCell>Blue</TableCell>
                                            <TableCell>Green</TableCell>
                                            <TableCell>Slim Jim</TableCell>
                                            <TableCell>Compost Bucket</TableCell>
                                            <TableCell>All</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <TableRow>
                                            <TableCell>{binCounts.black}</TableCell>
                                            <TableCell>{binCounts.blue}</TableCell>
                                            <TableCell>{binCounts.green}</TableCell>
                                            <TableCell>{binCounts.slimJim}</TableCell>
                                            <TableCell>{binCounts.compostBucket}</TableCell>
                                            <TableCell>{binCounts.all}</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </Paper>
                        </AccordionDetails>
                    </Accordion>
                </>
            )}

            {/* Packing List Section */}
            <Accordion
                expanded={expandedSections.has('packingList')}
                onChange={handleAccordionChange('packingList')}
                sx={{
                    width: '100%',
                    mt: 2,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    borderRadius: '8px',
                    border: '1px solid rgba(0,0,0,0.06)',
                    '&:before': {
                        display: 'none',
                    },
                }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <ChecklistIcon />
                        <Typography variant="h6" sx={{ fontWeight: 500 }}>
                            Packing List
                        </Typography>
                        <Typography variant="body2" sx={{ ml: 'auto', color: 'text.secondary' }}>
                            {/* Count will be calculated in EventInventoryPackingList */}
                        </Typography>
                        {isTargetOfActiveOp && (
                            <Button
                                variant="contained"
                                size="small"
                                component="div"
                                startIcon={<ChecklistIcon />}
                                onClick={e => {
                                    e.stopPropagation();
                                    handleViewConfirmation();
                                }}
                                sx={{ ml: 2 }}
                            >
                                View Packing Confirmation
                            </Button>
                        )}
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <EventInventoryPackingList
                        eventId={props.eventId}
                        onAddItem={() => {
                            // Will be implemented in task #430
                        }}
                        onCopyFromEvent={() => {
                            // Will be implemented in task #433
                        }}
                    />
                </AccordionDetails>
            </Accordion>

            {/* Consumables Section */}
            <Accordion
                expanded={expandedSections.has('consumables')}
                onChange={handleAccordionChange('consumables')}
                sx={{
                    width: '100%',
                    mt: 2,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    borderRadius: '8px',
                    border: '1px solid rgba(0,0,0,0.06)',
                    '&:before': {
                        display: 'none',
                    },
                }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <LocalGasStationIcon />
                        <Typography variant="h6" sx={{ fontWeight: 500 }}>
                            Consumables
                        </Typography>
                        <Typography variant="body2" sx={{ ml: 'auto', color: 'text.secondary' }}>
                            Track usage of consumable supplies
                        </Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <EventInventoryConsumables eventId={props.eventId} event={event} />
                </AccordionDetails>
            </Accordion>

            {/* Wish List Section */}
            <Accordion
                expanded={expandedSections.has('wishList')}
                onChange={handleAccordionChange('wishList')}
                sx={{
                    width: '100%',
                    mt: 2,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    borderRadius: '8px',
                    border: '1px solid rgba(0,0,0,0.06)',
                    '&:before': {
                        display: 'none',
                    },
                }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <StarIcon />
                        <Typography variant="h6" sx={{ fontWeight: 500 }}>
                            Wish List
                        </Typography>
                        <Typography variant="body2" sx={{ ml: 'auto', color: 'text.secondary' }}>
                            {/* Count will be calculated in EventInventoryWishList */}
                        </Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <EventInventoryWishList
                        eventId={props.eventId}
                        onAddItem={() => {
                            // Will be implemented in task #435
                        }}
                    />
                </AccordionDetails>
            </Accordion>
        </Container>
    );
}
